import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ArrowLeft, Check, Expand, X, ChevronLeft, ChevronRight } from "lucide-react";
import PageWrapper from "../components/PageWrapper";
import { FadeUp } from "../components/motion";
import { fetchProduct, fetchProducts, submitEnquiry } from "../lib/api";
import { toast, Toaster } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", location: "", timeline: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    setImgIdx(0);
    setLightbox(false);
    fetchProduct(slug).then((p) => (p ? setProduct(p) : navigate("/products")));
  }, [slug, navigate]);

  useEffect(() => {
    if (!product) return;
    fetchProducts({ category: product.category })
      .then((list) => setRelated(list.filter((p) => p.slug !== product.slug).slice(0, 3)))
      .catch(() => {});
  }, [product]);

  const handle = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await submitEnquiry({
        ...form,
        enquiry_type: "product",
        product_slug: product.slug,
        product_name: product.name,
      });
      toast.success("Enquiry received. We'll reach out within 24 hours.");
      setForm({ name: "", email: "", phone: "", location: "", timeline: "", message: "" });
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!product) {
    return (
      <PageWrapper>
        <div className="pt-[170px] pb-32 max-w-[1440px] mx-auto px-5 md:px-10">
          <div className="font-display text-3xl text-[#F2EDE3]/40">Loading product…</div>
        </div>
      </PageWrapper>
    );
  }

  const gallery = product.gallery && product.gallery.length ? product.gallery : [product.image];
  const showImg = gallery[Math.min(imgIdx, gallery.length - 1)];
  const step = (d) => setImgIdx((i) => (i + d + gallery.length) % gallery.length);

  return (
    <PageWrapper>
      <Toaster position="top-center" richColors />

      {/* Breadcrumb */}
      <section className="pt-[100px] pb-5 border-b border-[#D9A441]/12">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14 flex flex-wrap items-center gap-3 text-xs">
          <Link to="/products" data-testid="back-to-products" className="eyebrow text-[#F2EDE3]/55 hover:text-[#D9A441] inline-flex items-center gap-1.5 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Products
          </Link>
          <span className="text-[#F2EDE3]/25">/</span>
          <Link to={`/products/category/${product.category}`} className="eyebrow text-[#F2EDE3]/55 hover:text-[#D9A441] transition-colors">
            {product.category.replaceAll("-", " ")}
          </Link>
          <span className="text-[#F2EDE3]/25">/</span>
          <span className="eyebrow text-[#D9A441]">{product.name}</span>
        </div>
      </section>

      {/* Main split */}
      <section data-testid="product-detail">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 py-10 md:py-16">
          {/* LEFT: image — sticky */}
          <div className="lg:col-span-7">
            <div className="lg:sticky lg:top-[100px]">
              <FadeUp>
                {/* Main image — click to open full view */}
                <button
                  type="button"
                  onClick={() => setLightbox(true)}
                  data-testid="product-gallery-main"
                  aria-label="Open full-size image"
                  className="group/img relative block w-full overflow-hidden rounded-lg gold-frame cursor-zoom-in"
                >
                  <img src={showImg} alt={product.name} className="w-full h-[300px] md:h-[460px] object-cover transition-transform duration-700 group-hover/img:scale-[1.03]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0908]/50 to-transparent pointer-events-none" />
                  <span className="absolute top-5 left-5 eyebrow !text-[9px] bg-[#0A0908]/70 backdrop-blur-sm border border-[#D9A441]/40 text-[#D9A441] px-3 py-1.5 rounded-sm">
                    {product.code}
                  </span>
                  <span className="absolute bottom-5 right-5 eyebrow !text-[10px] bg-[#D9A441] text-[#0A0908] px-4 py-2 rounded-sm">
                    {product.size_range}
                  </span>
                  <span className="absolute bottom-5 left-5 w-9 h-9 rounded-full bg-[#0A0908]/70 backdrop-blur-sm border border-[#D9A441]/40 flex items-center justify-center opacity-80 group-hover/img:opacity-100 transition-opacity">
                    <Expand className="w-4 h-4 text-[#D9A441]" />
                  </span>
                </button>

                {/* Thumbnails */}
                <div className="mt-3 grid grid-cols-5 md:grid-cols-6 gap-2" data-testid="product-thumbnails">
                  {gallery.map((src, i) => (
                    <button
                      key={src}
                      type="button"
                      onClick={() => setImgIdx(i)}
                      data-testid={`product-thumb-${i}`}
                      aria-label={`View image ${i + 1}`}
                      className={`relative overflow-hidden rounded-md aspect-[4/3] border transition-all duration-300 ${
                        i === imgIdx ? "border-[#D9A441] opacity-100" : "border-white/10 opacity-55 hover:opacity-90"
                      }`}
                    >
                      <img src={src} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </FadeUp>

              {/* Configuration layers */}
              <FadeUp delay={0.1} className="mt-5 panel p-6 md:p-8">
                <div className="eyebrow text-[#D9A441] mb-4">Configuration Layers</div>
                <div className="grid grid-cols-2 gap-y-3 gap-x-6 text-sm">
                  {["Base Structure", "Envelope & Insulation", "MEP Kit", "Interior Package", "Bath & Kitchen", "Exterior Finish"].map((l) => (
                    <div key={l} className="flex items-center gap-2.5">
                      <span className="w-1.5 h-1.5 bg-[#D9A441] rounded-full" />
                      <span className="text-[#F2EDE3]/70">{l}</span>
                    </div>
                  ))}
                </div>
              </FadeUp>
            </div>
          </div>

          {/* RIGHT: details */}
          <div className="lg:col-span-5">
            <div className="eyebrow text-[#F2EDE3]/50 mb-3">{product.code} · {product.form}</div>
            <h1 data-testid="product-title" className="font-display text-4xl sm:text-5xl text-[#F2EDE3] leading-[1.08]">
              {product.name}
            </h1>
            <p className="mt-5 text-sm md:text-base text-[#F2EDE3]/60 leading-relaxed">{product.description}</p>

            {/* Specs grid */}
            <div className="mt-8 grid grid-cols-2 gap-px bg-[#D9A441]/15 border border-[#D9A441]/15 rounded-md overflow-hidden">
              {[
                ["Size Range", product.size_range],
                ["Format", product.form],
                ["Deployment", product.deployment],
                ["Price Band", product.price_range],
              ].map(([l, v]) => (
                <div key={l} className="bg-[#100F0C] p-5">
                  <div className="eyebrow !text-[9px] text-[#F2EDE3]/45 mb-2">{l}</div>
                  <div className="font-display text-lg text-[#F2EDE3]">{v}</div>
                </div>
              ))}
            </div>

            {/* Highlights */}
            <div className="mt-8">
              <div className="eyebrow text-[#D9A441] mb-4">Highlights</div>
              <ul className="space-y-3">
                {product.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-3 text-sm text-[#F2EDE3]/80">
                    <Check className="w-4 h-4 mt-0.5 text-[#D9A441] shrink-0" />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Use cases */}
            <div className="mt-8">
              <div className="eyebrow text-[#D9A441] mb-4">Ideal Use Cases</div>
              <div className="flex flex-wrap gap-2">
                {product.use_cases.map((u) => (
                  <span key={u} className="text-xs border border-[#D9A441]/30 text-[#F2EDE3]/75 px-4 py-2 rounded-sm">
                    {u}
                  </span>
                ))}
              </div>
            </div>

            {/* Enquiry form */}
            <form onSubmit={submit} data-testid="product-enquiry-form" className="mt-10 panel p-6 md:p-8">
              <div className="eyebrow text-[#D9A441] mb-2">Enquire about this model</div>
              <h3 className="font-display text-2xl text-[#F2EDE3] mb-6">Request quote &amp; specs</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input data-testid="enquiry-name" required value={form.name} onChange={handle("name")} placeholder="Full name *" className="field" />
                <input data-testid="enquiry-email" required type="email" value={form.email} onChange={handle("email")} placeholder="Email *" className="field" />
                <input data-testid="enquiry-phone" value={form.phone} onChange={handle("phone")} placeholder="Phone" className="field" />
                <input data-testid="enquiry-location" value={form.location} onChange={handle("location")} placeholder="Site location (city / state)" className="field" />
                <select data-testid="enquiry-timeline" value={form.timeline} onChange={handle("timeline")} className="field sm:col-span-2">
                  <option value="">Timeline</option>
                  <option>Immediate (within 60 days)</option>
                  <option>1-3 months</option>
                  <option>3-6 months</option>
                  <option>Exploring</option>
                </select>
                <textarea data-testid="enquiry-message" required value={form.message} onChange={handle("message")} rows={4} placeholder="Tell us about your site & vision *" className="field sm:col-span-2" />
              </div>

              <button type="submit" data-testid="enquiry-submit" disabled={submitting} className="btn-gold w-full mt-5 disabled:opacity-60">
                {submitting ? "Submitting…" : "Send Enquiry"} <ArrowUpRight className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-16 border-t border-[#D9A441]/12" data-testid="related-products">
          <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14">
            <div className="eyebrow eyebrow-dash text-[#D9A441] mb-3">More in this collection</div>
            <h3 className="font-display text-3xl text-[#F2EDE3] mb-10">Explore similar models</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to={`/products/${p.slug}`}
                  data-testid={`related-${p.slug}`}
                  className="group panel overflow-hidden hover:border-[#D9A441]/50 transition-colors duration-500"
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={p.image} alt={p.name} loading="lazy" className="product-card-img absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0908]/60 to-transparent" />
                  </div>
                  <div className="p-6 flex items-center justify-between gap-4">
                    <div>
                      <div className="text-xs text-[#F2EDE3]/50 mb-1">{p.form}</div>
                      <div className="font-display text-xl text-[#F2EDE3] group-hover:text-[#D9A441] transition-colors">{p.name}</div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-[#D9A441] transition-transform duration-300 group-hover:translate-x-1" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
      {/* Lightbox — full e-commerce style image view */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-[#050403]/95 backdrop-blur-sm flex items-center justify-center"
          data-testid="product-lightbox"
          onClick={() => setLightbox(false)}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightbox(false); }}
            data-testid="lightbox-close"
            aria-label="Close"
            className="absolute top-5 right-5 w-11 h-11 rounded-full border border-[#D9A441]/50 bg-[#0A0908]/70 flex items-center justify-center text-[#F2EDE3] hover:text-[#D9A441] hover:border-[#D9A441] transition-colors z-10"
          >
            <X className="w-5 h-5" />
          </button>
          {gallery.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); step(-1); }}
                data-testid="lightbox-prev"
                aria-label="Previous image"
                className="absolute left-3 md:left-6 w-11 h-11 rounded-full border border-[#D9A441]/50 bg-[#0A0908]/70 flex items-center justify-center text-[#F2EDE3] hover:text-[#D9A441] hover:border-[#D9A441] transition-colors z-10"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); step(1); }}
                data-testid="lightbox-next"
                aria-label="Next image"
                className="absolute right-3 md:right-6 w-11 h-11 rounded-full border border-[#D9A441]/50 bg-[#0A0908]/70 flex items-center justify-center text-[#F2EDE3] hover:text-[#D9A441] hover:border-[#D9A441] transition-colors z-10"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}
          <div className="max-w-[92vw] max-h-[86vh]" onClick={(e) => e.stopPropagation()}>
            <img src={showImg} alt={product.name} className="max-w-[92vw] max-h-[78vh] object-contain rounded-md" />
            <div className="mt-4 flex items-center justify-between text-xs text-[#F2EDE3]/60">
              <span className="font-display text-base text-[#F2EDE3]">{product.name}</span>
              <span data-testid="lightbox-counter">{Math.min(imgIdx, gallery.length - 1) + 1} / {gallery.length}</span>
            </div>
          </div>
        </div>
      )}
    </PageWrapper>
  );
}
